import { ChangeEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "i18next";
import {
  Button,
  ComposedModal,
  ContentSwitcher,
  Loading,
  ModalBody,
  ModalHeader,
  Select,
  SelectItem,
  Switch,
} from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import ChangePasswordForm from "./ChangePasswordForm";
import { userCompanyTypeMap } from "../../types";
import { useChangeLanguage } from "../../hooks/useUsers";
import { useNotification, useUser } from "../../context";
import { setCookie } from "../../utils";
import "./profile.scss";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "pt", label: "Português" },
];

/**
 * The self-profile screen (`/profile`), rebuilt on the Carbon layout primitives (Task 5,
 * Profile/Company flow) in place of 283 lines of Tailwind and a hand-rolled `<dl>`. Four
 * behaviours carried over unchanged from the pre-Carbon version:
 *
 * 1. **Read-only identity card** — name/role/company/email/phone, sourced from `useUser()`'s own
 *    `user`. Not editable here; the pencil affordance still links to `/users/{id}/edit` (the
 *    Users flow owns identity editing).
 * 2. **Company switcher** — only rendered when `companyList` (from `UserProvider`) has more than
 *    one entry, calling `changeCompany` straight through.
 * 3. **Change language** — a `<select>` bound to local state, applied via
 *    `useChangeLanguage`'s `PUT /user/{id}/{language}` and then the client-side
 *    `i18next.changeLanguage` + cookie/localStorage sync.
 * 4. **`isResetPassword` collapse** — the whole identity/company/language section hides and only
 *    the change-password form renders. That form now lives in `ChangePasswordForm.tsx`, which
 *    also carries the Task-2 contract change (a required, server-verified `currentPassword` —
 *    see that component's own docstring for the reset-mode exemption).
 */
const Profile = () => {
  const { openNotification } = useNotification();
  const {
    user,
    isResetPassword,
    isLoading,
    companyList,
    changeCompany,
  } = useUser();

  const { t, i18n } = useTranslation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Persist the choice to the backend in the background. No success toast / getUser() refetch:
  // the UI has already switched instantly client-side (below), so a refetch would only risk a
  // loading flash on this same screen.
  const { mutate: updateLanguage } = useChangeLanguage(
    () => undefined,
    () => openNotification(t("newMaterialForm.error"))
  );

  // The language actually in effect right now (i18next may report a region tag like "es-AR").
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    user?.localization ||
    "en"
  ).split("-")[0];

  const handleSelectLanguage = (value?: string) => {
    if (!value || value === currentLanguage) return;
    // Instant, no reload: flip i18next first (whole UI re-renders in the new language)...
    changeLanguage(value);
    setCookie("i18nextLng", value, 30);
    localStorage.setItem("i18nextLng", value);
    // ...then persist so it survives the next full load / other devices.
    if (user?.id) {
      updateLanguage({ userId: user.id, language: value });
    }
  };

  const handleCompanyChange = (companyId: string) => {
    if (companyId && companyId !== user?.company?.id) {
      changeCompany(companyId);
    }
  };

  return (
    <div>
      <PageHeader title={t("profile.title")} />

      {!isResetPassword && (
        <div className="profile-info cds-form-surface">
          <div className="profile-info__header">
            <h2>{t("profile.information")}</h2>
            <Button
              as={Link}
              to={`/users/${user?.id}/edit`}
              kind="ghost"
              size="sm"
              renderIcon={Edit}
            >
              {t("cta.edit")}
            </Button>
          </div>

          {isLoading ? (
            <Loading withOverlay={false} description={t("cta.loading")} small />
          ) : (
            <dl className="profile-info__list">
              <div className="profile-info__row">
                <dt>{t("profile.name")}</dt>
                <dd>{user?.fullName}</dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.role")}</dt>
                <dd>
                  {
                    userCompanyTypeMap[
                      user?.userCompanyType as keyof typeof userCompanyTypeMap
                    ]
                  }
                </dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.company")}</dt>
                <dd>
                  {user?.company?.name && companyList.length > 0 ? (
                    <Select
                      id="profile-company-switcher"
                      labelText={t("profile.company")}
                      hideLabel
                      value={user?.company?.id}
                      onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                        handleCompanyChange(event.target.value)
                      }
                    >
                      {companyList.map((company) => (
                        <SelectItem
                          key={company.id}
                          value={company.id}
                          text={company.name}
                        />
                      ))}
                    </Select>
                  ) : (
                    user?.company?.name
                  )}
                </dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.email")}</dt>
                <dd>{user?.email}</dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.phone")}</dt>
                <dd>{user?.phone}</dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.password")}</dt>
                <dd>
                  <Button
                    kind="tertiary"
                    size="sm"
                    onClick={() => setShowChangePassword(true)}
                  >
                    {t("profile.changePassword")}
                  </Button>
                </dd>
              </div>
              <div className="profile-info__row">
                <dt>{t("profile.language")}</dt>
                <dd className="profile-info__language">
                  <ContentSwitcher
                    size="md"
                    selectedIndex={Math.max(
                      0,
                      LANGUAGE_OPTIONS.findIndex(
                        (option) => option.value === currentLanguage
                      )
                    )}
                    onChange={({ name }) =>
                      handleSelectLanguage(name as string)
                    }
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <Switch
                        key={option.value}
                        name={option.value}
                        text={option.label}
                      />
                    ))}
                  </ContentSwitcher>
                </dd>
              </div>
            </dl>
          )}
        </div>
      )}

      {/* Forced reset (RESET_PASSWORD) stays a full inline page — there is no profile to return
          to. In normal mode, changing the password is a modal dialog over the profile. */}
      {isResetPassword ? (
        <ChangePasswordForm onCancel={() => setShowChangePassword(false)} />
      ) : (
        showChangePassword && (
          <ComposedModal
            open
            size="sm"
            onClose={() => setShowChangePassword(false)}
          >
            <ModalHeader title={t("profile.changePassword")} />
            <ModalBody>
              <ChangePasswordForm
                variant="dialog"
                onCancel={() => setShowChangePassword(false)}
              />
            </ModalBody>
          </ComposedModal>
        )
      )}
    </div>
  );
};

export default Profile;
